// Vedic & Chaldean Numerology Engine (अंक ज्योतिष)

export interface LoshuPlane {
  name: string;
  nameHi: string;
  numbers: number[];
  presentNumbers: number[];
  percentage: number;
  significance: string;
  significanceHi: string;
}

export interface NumerologyAnalysis {
  moolank: number; // Root / Driver Number (1-9)
  bhagyank: number; // Destiny / Life Path Number (1-9)
  namankChaldean: number; // Name Number Chaldean
  namankPythagorean: number; // Name Number Pythagorean
  kuaNumber: number;
  birthDateFormatted: string;
  
  // Planet & Deity Associations
  moolankLord: string;
  moolankLordHi: string;
  bhagyankLord: string;
  bhagyankLordHi: string;
  namankLord: string;
  
  // Traits & Interpretation
  moolankTraits: {
    title: string;
    titleHi: string;
    description: string;
    descriptionHi: string;
    strengths: string[];
    strengthsHi: string[];
    challenges: string[];
    challengesHi: string[];
  };
  
  bhagyankTraits: {
    title: string;
    titleHi: string;
    description: string;
    descriptionHi: string;
    karmicMission: string;
    karmicMissionHi: string;
  };

  // Loshu Grid
  loshuGrid: {
    matrix: (number | null)[][]; // 3x3
    countMap: Record<number, number>; // count of each digit 1-9
    planes: LoshuPlane[];
    missingNumbers: number[];
    repeatedNumbers: { num: number; count: number; meaning: string; meaningHi: string }[];
  };

  // Lucky Factors
  luckyNumbers: number[];
  neutralNumbers: number[];
  unluckyNumbers: number[];
  luckyColors: string[];
  luckyColorsHi: string[];
  luckyDays: string[];
  luckyDaysHi: string[];
  luckyDates: number[];
  favorableDirections: string[];
  favorableDirectionsHi: string[];
  recommendedGemstone: string;
  recommendedGemstoneHi: string;
  recommendedRudraksha: string;
  recommendedMantra: string;
  recommendedYantra: string;

  // Personal Year Cycle
  personalYear: number;
  personalYearForecast: string;
  personalYearForecastHi: string;

  // Special Numbers
  isMasterNumber?: number; // 11, 22, 33
  karmicDebtNumber?: number; // 13, 14, 16, 19

  // Input Validation & Provenance
  isNameValid: boolean;
  nameValidationMessage?: string;
  isDateValid: boolean;
  dateValidationMessage?: string;
  numerologyMethodology: string;
  traditionBadges: {
    moolank: string;
    bhagyank: string;
    namank: string;
    kua: string;
    loshu: string;
  };
}

export interface NameNumberResult {
  total: number;
  single: number;
  breakdown: { char: string; val: number }[];
  isValid: boolean;
  warning?: string;
}

export interface ParsedBirthDate {
  year: number;
  month: number;
  day: number;
  isValid: boolean;
  formatted: string;
  rawDigits: number[];
}

// Chaldean Letter Values (1-8, 9 is sacred and excluded)
export const CHALDEAN_MAP: Record<string, number> = {
  A: 1, I: 1, J: 1, Q: 1, Y: 1,
  B: 2, K: 2, R: 2,
  C: 3, G: 3, L: 3, S: 3,
  D: 4, M: 4, T: 4,
  E: 5, H: 5, N: 5, X: 5,
  U: 6, V: 6, W: 6,
  O: 7, Z: 7,
  F: 8, P: 8
};

// Pythagorean Letter Values (1-9 sequential)
export const PYTHAGOREAN_MAP: Record<string, number> = {
  A: 1, J: 1, S: 1,
  B: 2, K: 2, T: 2,
  C: 3, L: 3, U: 3,
  D: 4, M: 4, V: 4,
  E: 5, N: 5, W: 5,
  F: 6, O: 6, X: 6,
  G: 7, P: 7, Y: 7,
  H: 8, Q: 8, Z: 8,
  I: 9, R: 9
};

// Sum digits repeatedly to get single digit (1-9). Returns 0 if input <= 0.
export function reduceToSingleDigit(num: number): number {
  if (num <= 0) return 0;
  let current = num;
  while (current > 9) {
    let sum = 0;
    const str = current.toString();
    for (let i = 0; i < str.length; i++) {
      sum += parseInt(str[i], 10) || 0;
    }
    current = sum;
  }
  return current;
}

// Multi-stage reduction with Master Number (11, 22, 33) tracking
export function reduceWithMasterTracking(num: number): { single: number; masterNumber?: number } {
  if (num <= 0) return { single: 0 };
  let current = num;
  let detectedMaster: number | undefined;

  if (current === 11 || current === 22 || current === 33) {
    detectedMaster = current;
  }

  while (current > 9) {
    let sum = 0;
    const str = current.toString();
    for (let i = 0; i < str.length; i++) {
      sum += parseInt(str[i], 10) || 0;
    }
    if ((sum === 11 || sum === 22 || sum === 33) && !detectedMaster) {
      detectedMaster = sum;
    }
    current = sum;
  }

  return { single: current, masterNumber: detectedMaster };
}

// Parse and validate date across ISO, slash, and Date formats
export function parseAndValidateBirthDate(input: string | Date): ParsedBirthDate {
  let year = 1990;
  let month = 1;
  let day = 1;
  let isValid = false;

  if (input instanceof Date) {
    if (!isNaN(input.getTime())) {
      year = input.getFullYear();
      month = input.getMonth() + 1;
      day = input.getDate();
      isValid = true;
    }
  } else if (typeof input === 'string') {
    const trimmed = input.trim();
    // Check for ISO YYYY-MM-DD or YYYY/MM/DD
    const isoMatch = trimmed.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})/);
    if (isoMatch) {
      year = parseInt(isoMatch[1], 10);
      month = parseInt(isoMatch[2], 10);
      day = parseInt(isoMatch[3], 10);
      isValid = true;
    } else {
      // Check for DD/MM/YYYY or DD-MM-YYYY
      const dmyMatch = trimmed.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})/);
      if (dmyMatch) {
        day = parseInt(dmyMatch[1], 10);
        month = parseInt(dmyMatch[2], 10);
        year = parseInt(dmyMatch[3], 10);
        isValid = true;
      }
    }
  }

  // Validate calendar bounds and exact calendar month/day limits
  if (isValid) {
    if (year < 1000 || year > 2500 || month < 1 || month > 12 || day < 1 || day > 31) {
      isValid = false;
    } else {
      const checkDate = new Date(year, month - 1, day);
      if (
        checkDate.getFullYear() !== year ||
        checkDate.getMonth() !== month - 1 ||
        checkDate.getDate() !== day
      ) {
        isValid = false;
      }
    }
  }

  if (!isValid) {
    year = 1990;
    month = 1;
    day = 1;
  }

  // Collect all digits for Lo Shu Grid
  const digitsStr = `${year}${month.toString().padStart(2, '0')}${day.toString().padStart(2, '0')}`;
  const rawDigits = digitsStr.split('').map(d => parseInt(d, 10)).filter(d => d >= 1 && d <= 9);
  const formatted = `${day.toString().padStart(2, '0')}/${month.toString().padStart(2, '0')}/${year}`;

  return { year, month, day, isValid, formatted, rawDigits };
}

// Calculate Chaldean Name Number
export function calculateChaldeanNameNumber(name: string): NameNumberResult {
  const clean = (name || '').toUpperCase().replace(/[^A-Z]/g, '');
  if (!clean) {
    return {
      total: 0,
      single: 0,
      breakdown: [],
      isValid: false,
      warning: 'Name contains no Latin letters (A-Z). Chaldean calculation requires Latin transliteration.'
    };
  }

  let total = 0;
  const breakdown: { char: string; val: number }[] = [];

  for (const ch of clean) {
    const val = CHALDEAN_MAP[ch] || 0;
    total += val;
    breakdown.push({ char: ch, val });
  }

  const single = reduceToSingleDigit(total);
  return { total, single, breakdown, isValid: true };
}

// Calculate Pythagorean Name Number
export function calculatePythagoreanNameNumber(name: string): { total: number; single: number; isValid: boolean; warning?: string } {
  const clean = (name || '').toUpperCase().replace(/[^A-Z]/g, '');
  if (!clean) {
    return {
      total: 0,
      single: 0,
      isValid: false,
      warning: 'Name contains no Latin letters (A-Z). Pythagorean calculation requires Latin transliteration.'
    };
  }

  let total = 0;
  for (const ch of clean) {
    total += PYTHAGOREAN_MAP[ch] || 0;
  }
  return { total, single: reduceToSingleDigit(total), isValid: true };
}

// Numerology Planet Lords
export const NUMBER_LORDS: Record<number, { en: string; hi: string }> = {
  1: { en: 'Sun (Surya)', hi: 'सूर्य (Surya)' },
  2: { en: 'Moon (Chandra)', hi: 'चंद्र (Chandra)' },
  3: { en: 'Jupiter (Brihaspati)', hi: 'गुरु (Brihaspati)' },
  4: { en: 'Rahu (North Node)', hi: 'राहु (Rahu)' },
  5: { en: 'Mercury (Budha)', hi: 'बुध (Budha)' },
  6: { en: 'Venus (Shukra)', hi: 'शुक्र (Shukra)' },
  7: { en: 'Ketu (South Node)', hi: 'केतु (Ketu)' },
  8: { en: 'Saturn (Shani)', hi: 'शनि (Shani)' },
  9: { en: 'Mars (Mangal)', hi: 'मंगल (Mangal)' }
};

// Relationship compatibility matrix
export const NUMBER_RELATIONS: Record<number, { friends: number[]; neutral: number[]; enemies: number[] }> = {
  1: { friends: [1, 2, 3, 5, 9], neutral: [4, 7], enemies: [6, 8] },
  2: { friends: [1, 2, 3, 5], neutral: [7, 9], enemies: [4, 6, 8] },
  3: { friends: [1, 2, 3, 5, 9], neutral: [8], enemies: [4, 6, 7] },
  4: { friends: [1, 5, 6, 7, 8], neutral: [2, 9], enemies: [3, 4] },
  5: { friends: [1, 2, 3, 5, 6], neutral: [7, 8, 9], enemies: [4] },
  6: { friends: [4, 5, 6, 7, 8], neutral: [3, 9], enemies: [1, 2] },
  7: { friends: [1, 4, 5, 6], neutral: [2, 3, 8], enemies: [7, 9] },
  8: { friends: [3, 4, 5, 6, 7], neutral: [2], enemies: [1, 8, 9] },
  9: { friends: [1, 2, 3, 5], neutral: [4, 6, 7], enemies: [8, 9] }
};

// Traits by Root Number
export const MOOLANK_DATA: Record<number, {
  title: string;
  titleHi: string;
  desc: string;
  descHi: string;
  strengths: string[];
  strengthsHi: string[];
  challenges: string[];
  challengesHi: string[];
  gemstone: string;
  gemstoneHi: string;
  rudraksha: string;
  mantra: string;
  yantra: string;
  colors: string[];
  colorsHi: string[];
  days: string[];
  daysHi: string[];
  dates: number[];
}> = {
  1: {
    title: 'The Sovereign Leader (नेतृत्व एवं आत्मबल)',
    titleHi: 'सूर्य प्रधान - तेजस्वी, दृढ़ निश्चयी एवं जन्मजात नेता',
    desc: 'Ruled by the Sun. Natives with Moolank 1 possess commanding aura, boundless vitality, independent spirit, executive clarity, and pioneering ambition.',
    descHi: 'सूर्य द्वारा शासित। मूलांक 1 के जातक ओजस्वी, स्वाभिमानी, नेतृत्व क्षमता से परिपूर्ण और स्वतंत्र विचारों वाले होते हैं।',
    strengths: ['Natural Leadership', 'High Self-Respect', 'Decisive Vision', 'Originality'],
    strengthsHi: ['अद्भुत नेतृत्व क्षमता', 'उच्च स्वाभिमान', 'निर्णय लेने में स्पष्टता', 'मौलिक दृष्टिकोण'],
    challenges: ['Ego clashes', 'Impatience with slower minds', 'Domineering nature'],
    challengesHi: ['अहंकार टकराव', 'धैर्य की कमी', 'हठी स्वभाव'],
    gemstone: 'Ruby (माणिक्य)',
    gemstoneHi: 'माणिक्य (Ruby)',
    rudraksha: '1 Mukhi or 12 Mukhi Rudraksha',
    mantra: 'ॐ ह्रीं सूर्याय नमः (Om Hreem Suryaya Namah)',
    yantra: 'Surya Yantra',
    colors: ['Gold', 'Orange', 'Ruby Red', 'Yellow'],
    colorsHi: ['सुनहरा', 'नारंगी', 'गहरा लाल', 'पीला'],
    days: ['Sunday', 'Monday'],
    daysHi: ['रविवार', 'सोमवार'],
    dates: [1, 10, 19, 28]
  },
  2: {
    title: 'The Intuitive Diplomat (सौम्य, संवेदनशील एवं रचनाकार)',
    titleHi: 'चंद्र प्रधान - कोमल हृदय, कल्पनाशील एवं शांतिप्रिय',
    desc: 'Ruled by the Moon. Empathetic, artistic, emotionally profound, adaptable, and naturally gifted in diplomacy, psychology, and collaborative creation.',
    descHi: 'चंद्रमा द्वारा शासित। मूलांक 2 के जातक संवेदनशील, कल्पनाशील, शांतिप्रिय, कलात्मक और दूसरों की भावनाओं को तुरंत समझने वाले होते हैं।',
    strengths: ['Deep Intuition', 'Artistic Refinement', 'Cooperative Charm', 'Empathy'],
    strengthsHi: ['तीक्ष्ण अंतर्ज्ञान', 'कलात्मक अभिरुचि', 'मधुर वाणी व सामंजस्य', 'सहानुभूति'],
    challenges: ['Mood swings', 'Over-sensitivity', 'Indecisiveness under stress'],
    challengesHi: ['मन की चंचलता', 'अति-संवेदनशीलता', 'दुविधाग्रस्त मन'],
    gemstone: 'Pearl (मोती) / Moonstone',
    gemstoneHi: 'सच्चा मोती (Pearl) अथवा चंद्रमणि',
    rudraksha: '2 Mukhi Rudraksha',
    mantra: 'ॐ सों सोमाय नमः (Om Som Somaya Namah)',
    yantra: 'Chandra Yantra',
    colors: ['Pearl White', 'Silver', 'Cream', 'Light Green'],
    colorsHi: ['मोतिया सफेद', 'चांदी जैसा', 'हल्का हरा', 'क्रीम'],
    days: ['Monday', 'Sunday'],
    daysHi: ['सोमवार', 'रविवार'],
    dates: [2, 11, 20, 29]
  },
  3: {
    title: 'The Wise Counselor (ज्ञान, गुरु कृपा एवं विस्तार)',
    titleHi: 'बृहस्पति प्रधान - ज्ञानवान, दूरदर्शी, धर्मपरायण एवं लोकप्रिय',
    desc: 'Ruled by Jupiter. Optimistic, scholarly, spiritually minded, noble, communicative, and blessed with mentor wisdom and expansive financial fortune.',
    descHi: 'देवगुरु बृहस्पति द्वारा शासित। मूलांक 3 के जातक ज्ञानी, स्पष्ट वक्ता, समाज में सम्मानित, नीतिवान एवं सलाह देने में दक्ष होते हैं।',
    strengths: ['Spiritual Wisdom', 'Inspiring Oratory', 'Optimism', 'Broad Vision'],
    strengthsHi: ['आत्मिक व बौद्धिक ज्ञान', 'प्रभावशाली वक्तृत्व', 'सकारात्मकता', 'दूरदर्शिता'],
    challenges: ['Scattered energies', 'Extravagance', 'Over-advising others'],
    challengesHi: ['अनावश्यक खर्च', 'कई कार्यों में उलझना', 'अहंकार'],
    gemstone: 'Yellow Sapphire (पुखराज)',
    gemstoneHi: 'पीला पुखराज (Yellow Sapphire)',
    rudraksha: '5 Mukhi Rudraksha',
    mantra: 'ॐ ग्रां ग्रीं ग्रौं सः गुरवे नमः (Om Gram Greem Grom Sah Gurave Namah)',
    yantra: 'Brihaspati Yantra',
    colors: ['Bright Yellow', 'Saffron', 'Gold', 'Amber'],
    colorsHi: ['पीला', 'केसरिया', 'सुनहरा', 'हल्का नारंगी'],
    days: ['Thursday', 'Tuesday'],
    daysHi: ['गुरुवार', 'मंगलवार'],
    dates: [3, 12, 21, 30]
  },
  4: {
    title: 'The Strategic Revolutionary (परिश्रमी, योजनाकार एवं खोजी)',
    titleHi: 'राहु प्रधान - मौलिक विचारक, क्रांतिकारी, परिश्रमी एवं विश्लेषक',
    desc: 'Ruled by Rahu. Sharp unconventional intellect, master strategist, structured worker, out-of-the-box thinker, and transformer of obsolete systems.',
    descHi: 'राहु द्वारा शासित। मूलांक 4 के जातक विलक्षण बुद्धि, तकनीकी सोच, शोधकर्ता, परंपराओं से हटकर सोचने वाले और कर्मठ होते हैं।',
    strengths: ['Unconventional Genius', 'Structural Discipline', 'Reliability', 'Tenacity'],
    strengthsHi: ['अद्वितीय सोच', 'व्यावहारिक योजना', 'लगन व परिश्रम', 'तकनीकी दक्षता'],
    challenges: ['Sudden mood swings', 'Rebellious conflicts', 'Self-isolation'],
    challengesHi: ['अप्रत्याशित बदलाव', 'अति-आलोचनात्मक दृष्टिकोण', 'अकेलापन'],
    gemstone: 'Hessonite Garnet (गोमेद)',
    gemstoneHi: 'गोमेद (Hessonite Garnet)',
    rudraksha: '8 Mukhi Rudraksha',
    mantra: 'ॐ भ्रां भ्रीं भ्रौं सः राहवे नमः (Om Bhram Bhreem Bhrom Sah Rahave Namah)',
    yantra: 'Rahu Yantra',
    colors: ['Electric Blue', 'Smoky Grey', 'Silver Grey'],
    colorsHi: ['नीला', 'धूसर (Grey)', 'खाकी'],
    days: ['Saturday', 'Sunday'],
    daysHi: ['शनिवार', 'रविवार'],
    dates: [4, 13, 22, 31]
  },
  5: {
    title: 'The Dynamic Communicator (बुद्धि, वाणिज्य एवं गतिशीलता)',
    titleHi: 'बुध प्रधान - वाकपटु, चतुर व्यापारी, त्वरित बुद्धि एवं जिज्ञासु',
    desc: 'Ruled by Mercury. Fast-witted, commercially astute, charismatic, versatile, master of networking, humor, business, and quick adaptability.',
    descHi: 'बुध द्वारा शासित। मूलांक 5 के जातक त्वरित निर्णय लेने वाले, बहुमुखी प्रतिभा संपन्न, हाजिरजवाब, व्यापार में निपुण और मिलनसार होते हैं।',
    strengths: ['Commercial Genius', 'Quick Wit', 'Adaptability', 'Persuasion'],
    strengthsHi: ['व्यापारिक कुशलता', 'हाजिरजवाबी', 'अनुकूलन क्षमता', 'आकर्षण'],
    challenges: ['Restlessness', 'Fickle commitments', 'Nervous exhaustion'],
    challengesHi: ['चंचल मन', 'धैर्य का अभाव', 'जल्दबाजी में निर्णय'],
    gemstone: 'Emerald (पन्ना)',
    gemstoneHi: 'पन्ना (Emerald)',
    rudraksha: '4 Mukhi or 10 Mukhi Rudraksha',
    mantra: 'ॐ ब्रां ब्रीं ब्रौं सः बुधाय नमः (Om Bram Breem Brom Sah Budhaya Namah)',
    yantra: 'Budh Yantra',
    colors: ['Emerald Green', 'Light Turquoise', 'Pistachio'],
    colorsHi: ['हरा', 'तोतिया हरा', 'हल्का फिरोजी'],
    days: ['Wednesday', 'Friday'],
    daysHi: ['बुधवार', 'शुक्रवार'],
    dates: [5, 14, 23]
  },
  6: {
    title: 'The Magnet of Beauty & Luxury (सौंदर्य, प्रेम एवं ऐश्वर्य)',
    titleHi: 'शुक्र प्रधान - कलात्मक, सौन्दर्य प्रेमी, आकर्षक एवं समृद्धि कारक',
    desc: 'Ruled by Venus. Refined aesthetic taste, magnetism, generosity, romantic devotion, domestic elegance, and naturally attracting comfort and wealth.',
    descHi: 'दैत्यगुरु शुक्र द्वारा शासित। मूलांक 6 के जातक कलाप्रेमी, आकर्षक व्यक्तित्व, सुख-साधनों के शौकीन, दयालु और परिवार से गहरा लगाव रखने वाले होते हैं।',
    strengths: ['Magnetic Charm', 'Artistic Flair', 'Warm Generosity', 'Social Harmony'],
    strengthsHi: ['आकर्षक व्यक्तित्व', 'कलात्मक सुरुचि', 'उदारता व परोपकार', 'सदा हंसमुख'],
    challenges: ['Over-indulgence in luxury', 'Martyr syndrome', 'Possessiveness'],
    challengesHi: ['विलासिता में अति', 'हठ', 'अत्यधिक मोह'],
    gemstone: 'Diamond (हीरा) / White Zircon / Opal',
    gemstoneHi: 'हीरा (Diamond), ओपल अथवा सफेद जरकन',
    rudraksha: '6 Mukhi Rudraksha',
    mantra: 'ॐ द्रां द्रीं द्रौं सः शुक्राय नमः (Om Dram Dreem Drom Sah Shukraya Namah)',
    yantra: 'Shukra Yantra',
    colors: ['Sparkling White', 'Sky Blue', 'Pastel Pink', 'Cream'],
    colorsHi: ['चमकीला सफेद', 'आसमानी', 'गुलाबी', 'सिल्वर'],
    days: ['Friday', 'Wednesday'],
    daysHi: ['शुक्रवार', 'बुधवार'],
    dates: [6, 15, 24]
  },
  7: {
    title: 'The Mystic Philosopher (रहस्य, अध्यात्म एवं अंतर्मुखी ज्ञानी)',
    titleHi: 'केतु प्रधान - दार्शनिक, शोधार्थी, अंतर्ज्ञानी एवं आध्यात्मिक',
    desc: 'Ruled by Ketu. Deep introspective mind, psychic sensitivity, spiritual detachment, profound analytical curiosity, and natural seeker of truth.',
    descHi: 'केतु द्वारा शासित। मूलांक 7 के जातक गहन विचारक, खोजी प्रवृत्ति, ध्यान व योग में रुचि, गुप्त विधाओं के ज्ञाता और स्वतंत्र मन वाले होते हैं।',
    strengths: ['Psychic Intuition', 'Deep Research Depth', 'Spiritual Clarity', 'Wisdom'],
    strengthsHi: ['तीक्ष्ण पूर्वाभास', 'गहन अनुसंधान', 'आध्यात्मिक शांति', 'दार्शनिक सोच'],
    challenges: ['Aloofness', 'Over-analyzing simple matters', 'Skepticism'],
    challengesHi: ['एकांतप्रियता', 'अत्यधिक सोचना', 'संदेह की भावना'],
    gemstone: 'Cat\'s Eye (लहसुनिया)',
    gemstoneHi: 'लहसुनिया (Cat\'s Eye)',
    rudraksha: '9 Mukhi Rudraksha',
    mantra: 'ॐ स्रां स्रीं स्रौं सः केतवे नमः (Om Sram Sreem Srom Sah Ketave Namah)',
    yantra: 'Ketu Yantra',
    colors: ['White', 'Light Sea Green', 'Pastel Yellow'],
    colorsHi: ['सफेद', 'हल्का हरा', 'चितकबरा'],
    days: ['Sunday', 'Monday'],
    daysHi: ['रविवार', 'सोमवार'],
    dates: [7, 16, 25]
  },
  8: {
    title: 'The Master of Karma & Authority (कर्मठ, न्यायप्रिय एवं कर्मफल साधक)',
    titleHi: 'शनि प्रधान - न्यायप्रिय, धैर्यवान, संघर्षशील एवं दीर्घकालिक विजेता',
    desc: 'Ruled by Saturn. Indomitable resilience, deep sense of duty, justice-driven, master of slow but unshakable material and spiritual empire building.',
    descHi: 'कर्मफलदाता शनि द्वारा शासित। मूलांक 8 के जातक अत्यंत परिश्रमी, गंभीर, न्यायप्रिय, संघर्षों से न घबराने वाले और दीर्घकाल में बड़ी सफलता पाने वाले होते हैं।',
    strengths: ['Unbreakable Perseverance', 'Organizational Mastery', 'Fairness', 'Endurance'],
    strengthsHi: ['अटूट धैर्य व लगन', 'प्रबंधन क्षमता', 'सच्चाई व निष्पक्षता', 'गंभीरता'],
    challenges: ['Early life delays & hardships', 'Solitude', 'Stiffness'],
    challengesHi: ['आरंभिक संघर्ष व विलंब', 'एकाकीपन', 'कठोर वाणी'],
    gemstone: 'Blue Sapphire (नीलम) / Amethyst (जमुनिया)',
    gemstoneHi: 'नीलम (Blue Sapphire) अथवा जमुनिया',
    rudraksha: '7 Mukhi or 14 Mukhi Rudraksha',
    mantra: 'ॐ प्रां प्रीं प्रौं सः शनैश्चराय नमः (Om Pram Preem Prom Sah Shanaishcharaya Namah)',
    yantra: 'Shani Yantra',
    colors: ['Dark Navy Blue', 'Black', 'Steel Grey', 'Dark Violet'],
    colorsHi: ['गहरा नीला', 'काला', 'जामुनी', 'गहरा स्लेटी'],
    days: ['Saturday', 'Friday'],
    daysHi: ['शनिवार', 'शुक्रवार'],
    dates: [8, 17, 26]
  },
  9: {
    title: 'The Valiant Warrior & Humanitarian (साहस, ऊर्जा एवं वैश्विक दृष्टि)',
    titleHi: 'मंगल प्रधान - पराक्रमी, ऊर्जावान, साहसी एवं जन-कल्याणकारी',
    desc: 'Ruled by Mars. High vitality, fearless courage, defensive prowess, passionate humanitarian spirit, champion of the underprivileged.',
    descHi: 'सेनापति मंगल द्वारा शासित। मूलांक 9 के जातक साहसी, स्वाभिमानी, ऊर्जा से भरपूर, समाज सेवा में आगे रहने वाले और चुनौतियों का डटकर सामना करने वाले होते हैं।',
    strengths: ['Boundless Courage', 'Generous Protection', 'High Energy', 'Resilience'],
    strengthsHi: ['अद्वितीय साहस', 'परोपकार', 'अथाह ऊर्जा', 'नेतृत्व'],
    challenges: ['Short temper', 'Impulsive aggression', 'Risk-taking without thought'],
    challengesHi: ['शीघ्र क्रोध आना', 'उतावलापन', 'आक्रामकता'],
    gemstone: 'Red Coral (मूंगा)',
    gemstoneHi: 'लाल मूंगा (Red Coral)',
    rudraksha: '3 Mukhi Rudraksha',
    mantra: 'ॐ क्रां क्रीं क्रौं सः भौमाय नमः (Om Kram Kreem Krom Sah Bhaumaya Namah)',
    yantra: 'Mangal Yantra',
    colors: ['Crimson Red', 'Maroon', 'Bright Scarlet'],
    colorsHi: ['लाल', 'महरून', 'गुलाबी'],
    days: ['Tuesday', 'Thursday'],
    daysHi: ['मंगलवार', 'गुरुवार'],
    dates: [9, 18, 27]
  }
};

// Calculate Kua Number (Eight Mansions Feng Shui & Classical Ba Zhai Directional Energy)
export function calculateKuaNumber(birthYear: number, gender: 'male' | 'female' | 'other'): number {
  const isPost2000 = birthYear >= 2000;
  const last2Digits = Math.abs(birthYear) % 100;
  const reducedLast2 = last2Digits === 0 ? 0 : reduceToSingleDigit(last2Digits);

  if (gender === 'female') {
    let femaleKua = isPost2000
      ? reduceToSingleDigit(reducedLast2 + 6)
      : reduceToSingleDigit(reducedLast2 + 5);
    if (femaleKua === 5) femaleKua = 8;
    return femaleKua;
  } else {
    // male or other (defaults to male per standard convention)
    let maleKua = isPost2000
      ? 9 - reducedLast2
      : 10 - reducedLast2;
    while (maleKua > 9) maleKua = reduceToSingleDigit(maleKua);
    if (maleKua <= 0) maleKua = 9;
    if (maleKua === 5) maleKua = 2;
    return maleKua;
  }
}

// Calculate Loshu Grid Planes (3x3 Magic Square)
export function buildLoshuGrid(birthDateStr: string | Date): {
  matrix: (number | null)[][];
  countMap: Record<number, number>;
  planes: LoshuPlane[];
  missingNumbers: number[];
  repeatedNumbers: { num: number; count: number; meaning: string; meaningHi: string }[];
} {
  const parsed = parseAndValidateBirthDate(birthDateStr);
  const digits = parsed.rawDigits;
  
  const countMap: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0, 8: 0, 9: 0 };
  for (const d of digits) {
    if (d >= 1 && d <= 9) {
      countMap[d] = (countMap[d] || 0) + 1;
    }
  }

  // Standard Loshu Grid Layout:
  // [4, 9, 2]
  // [3, 5, 7]
  // [8, 1, 6]
  const matrix: (number | null)[][] = [
    [countMap[4] > 0 ? 4 : null, countMap[9] > 0 ? 9 : null, countMap[2] > 0 ? 2 : null],
    [countMap[3] > 0 ? 3 : null, countMap[5] > 0 ? 5 : null, countMap[7] > 0 ? 7 : null],
    [countMap[8] > 0 ? 8 : null, countMap[1] > 0 ? 1 : null, countMap[6] > 0 ? 6 : null]
  ];

  // 8 Planes
  const planeDefs = [
    {
      name: 'Mental Plane (मानसिक तल)',
      nameHi: 'मानसिक तल (स्मृति, बौद्धिक क्षमता व विवेक)',
      numbers: [4, 9, 2],
      significance: 'High memory retention, analytical clarity, and sharp intellectual vision.',
      significanceHi: 'उत्कृष्ट स्मरण शक्ति, गहन विश्लेषण क्षमता और तीक्ष्ण बौद्धिक प्रतिभा।'
    },
    {
      name: 'Emotional Plane (भावनात्मक तल)',
      nameHi: 'भावनात्मक व आत्मिक तल (हृदय, प्रेम व अंतर्ज्ञान)',
      numbers: [3, 5, 7],
      significance: 'Emotional balance, intuitive empathy, spiritual sensitivity, and artistic depth.',
      significanceHi: 'संतुलित भावनाएं, गहरी संवेदना, दूसरों की पीड़ा समझने की क्षमता व आध्यात्मिक समझ।'
    },
    {
      name: 'Practical / Physical Plane (व्यावहारिक तल)',
      nameHi: 'व्यावहारिक तल (कर्मठता, भौतिक सफलता व धरातल पर क्रियान्वयन)',
      numbers: [8, 1, 6],
      significance: 'Grounded practicality, relentless execution, financial acumen, and organizational mastery.',
      significanceHi: 'यथार्थवादी दृष्टिकोण, कार्य को पूरा करने की दृढ़ता और भौतिक समृद्धि।'
    },
    {
      name: 'Thought / Vision Plane (विचार तल)',
      nameHi: 'विचार तल (योजना, रचनात्मक सोच व दूरगामी विचार)',
      numbers: [4, 3, 8],
      significance: 'Strategic planning, foresight, structuring big concepts, and philosophical depth.',
      significanceHi: 'गंभीर विचारशीलता, दूरदर्शी योजनाएं और संकल्प शक्ति।'
    },
    {
      name: 'Willpower Plane (इच्छाशक्ति तल)',
      nameHi: 'इच्छाशक्ति तल (दृढ़ संकल्प, लगन व अटूट आत्मविश्वास)',
      numbers: [9, 5, 1],
      significance: 'Unshakable determination, resilience against hurdles, and self-made triumphant destiny.',
      significanceHi: 'अडिग इच्छाशक्ति, बाधाओं से पार पाने का अदम्य साहस और विजय।'
    },
    {
      name: 'Action Plane (कर्म तल)',
      nameHi: 'कर्म तल (त्वरित निर्णय, क्रियान्वयन व सक्रियता)',
      numbers: [2, 7, 6],
      significance: 'Rapid physical execution, converting ideas into living reality without procrastination.',
      significanceHi: 'बिना विलंब किए योजना को कर्म में बदलना, सक्रियता व गतिशीलता।'
    },
    {
      name: 'Golden Raj Yoga Plane (स्वर्ण राजयोग तल)',
      nameHi: 'स्वर्ण राजयोग (4-5-6) - सर्वांगीण सफलता, समृद्धि व ऐश्वर्य',
      numbers: [4, 5, 6],
      significance: 'Auspicious balance of Rahu (Strategy), Mercury (Business), and Venus (Wealth) creating continuous abundance.',
      significanceHi: 'रणनीति, व्यापार बुद्धि और ऐश्वर्य का महा-संयोग जो जीवन में अटूट सफलता प्रदान करता है।'
    },
    {
      name: 'Silver Raj Yoga Plane (रजत राजयोग तल)',
      nameHi: 'रजत राजयोग (2-5-8) - भूमि, भवन, अचल संपत्ति व स्थायित्व',
      numbers: [2, 5, 8],
      significance: 'Earth element triangle bringing monumental real estate success, property ownership, and enduring stability.',
      significanceHi: 'भूमि, भवन, अचल संपत्ति और जीवन में स्थायी संपन्नता का अत्यंत शुभ योग।'
    }
  ];

  const planes: LoshuPlane[] = planeDefs.map(pd => {
    const present = pd.numbers.filter(n => countMap[n] > 0);
    const percentage = Math.round((present.length / pd.numbers.length) * 100);
    return {
      name: pd.name,
      nameHi: pd.nameHi,
      numbers: pd.numbers,
      presentNumbers: present,
      percentage,
      significance: pd.significance,
      significanceHi: pd.significanceHi
    };
  });

  const missingNumbers: number[] = [];
  for (let n = 1; n <= 9; n++) {
    if (!countMap[n] || countMap[n] === 0) {
      missingNumbers.push(n);
    }
  }

  const repeatedMap: Record<number, { meaning: string; meaningHi: string }> = {
    1: {
      meaning: 'High expressiveness, talkative, magnetic persona, leadership Drive.',
      meaningHi: 'प्रखर संवाद कौशल, चुंबकीय व्यक्तित्व, नेतृत्व की तीव्र इच्छा।'
    },
    2: {
      meaning: 'Hypersensitive intuition, artistic creativity, mood fluctuations.',
      meaningHi: 'अति-संवेदनशील अंतर्ज्ञान, कलात्मक प्रतिभा, मन की चंचलता।'
    },
    3: {
      meaning: 'Prolific imagination, deep literary talent, philosophical expansion.',
      meaningHi: 'प्रचुर कल्पनाशीलता, गहन ज्ञान, लेखन व शिक्षण में विशेष रुचि।'
    },
    4: {
      meaning: 'Extreme perfectionism, relentless taskmaster, structural mastery.',
      meaningHi: 'अत्यधिक अनुशासन, पूर्णतावादी दृष्टिकोण, तकनीकी दक्षता।'
    },
    5: {
      meaning: 'Restless dynamic energy, wanderlust, sharp commercial versatility.',
      meaningHi: 'असीम ऊर्जा, बहुमुखी प्रतिभा, यात्रा प्रिय, तीव्र व्यापारिक बुद्धि।'
    },
    6: {
      meaning: 'Great devotion to luxury, aesthetic design, family responsibility.',
      meaningHi: 'सौंदर्य व कला के प्रति गहरा अनुराग, पारिवारिक दायित्वों का निर्वहन।'
    },
    7: {
      meaning: 'Occult depths, spiritual detachment, deep analytical researcher.',
      meaningHi: 'गूढ़ विद्याओं में रुचि, आध्यात्मिक झुकाव, अनुसंधान में महारत।'
    },
    8: {
      meaning: 'Heavy responsibilities, iron determination, slow compound wealth.',
      meaningHi: 'बड़ी जिम्मेदारियां, अटूट इच्छाशक्ति, स्थिर संपत्ति निर्माण।'
    },
    9: {
      meaning: 'Fierce humanitarian fire, courage, impatience with mediocrity.',
      meaningHi: 'साहस व ऊर्जा की अधिकता, परोपकारी भावना, उतावलापन।'
    }
  };

  const repeatedNumbers = Object.entries(countMap)
    .filter(([_, count]) => count >= 2)
    .map(([numStr, count]) => {
      const num = parseInt(numStr, 10);
      return {
        num,
        count,
        meaning: repeatedMap[num]?.meaning || 'Amplified vibrational intensity.',
        meaningHi: repeatedMap[num]?.meaningHi || 'इस अंक की ऊर्जा का विशेष प्रभाव।'
      };
    });

  return { matrix, countMap, planes, missingNumbers, repeatedNumbers };
}

// Master Function to Calculate Complete Vedic Numerology
export function calculateNumerology(
  birthDate: string | Date,
  fullName: string,
  gender: 'male' | 'female' | 'other' = 'male'
): NumerologyAnalysis {
  const parsedDate = parseAndValidateBirthDate(birthDate);
  const { year, month, day, isValid: isDateValid, formatted: birthDateFormatted } = parsedDate;

  // 1. Moolank (Day of birth 1-31 -> 1-9)
  const moolankReduction = reduceWithMasterTracking(day);
  const moolank = moolankReduction.single || 1;

  // 2. Bhagyank (Full date sum -> 1-9)
  let fullDateSum = 0;
  parsedDate.rawDigits.forEach(d => {
    fullDateSum += d;
  });
  const bhagyankReduction = reduceWithMasterTracking(fullDateSum);
  const bhagyank = bhagyankReduction.single || 1;

  // 3. Name numbers (Chaldean & Pythagorean)
  const chaldean = calculateChaldeanNameNumber(fullName);
  const pythagorean = calculatePythagoreanNameNumber(fullName);

  // 4. Kua Number (Century-aware Ba Zhai)
  const kuaNumber = calculateKuaNumber(year, gender);

  // 5. Loshu Grid
  const loshu = buildLoshuGrid(birthDate);

  // 6. Personal Year (Day + Month + Current Year)
  const currentYear = new Date().getFullYear();
  const personalYearSum = reduceToSingleDigit(day + month + reduceToSingleDigit(currentYear)) || 1;

  // 7. Master & Karmic Numbers Check
  let isMasterNumber: number | undefined;
  // Day 11, 22, 29 (2+9=11) carry Master Number root
  if (day === 11 || day === 22 || day === 29) {
    isMasterNumber = (day === 29 ? 11 : day);
  } else if (bhagyankReduction.masterNumber) {
    isMasterNumber = bhagyankReduction.masterNumber;
  }

  let karmicDebtNumber: number | undefined;
  if ([13, 14, 16, 19].includes(day)) {
    karmicDebtNumber = day;
  }

  const mData = MOOLANK_DATA[moolank] || MOOLANK_DATA[1];
  const bData = MOOLANK_DATA[bhagyank] || MOOLANK_DATA[1];
  const relations = NUMBER_RELATIONS[moolank] || NUMBER_RELATIONS[1];

  // Personal Year Forecast description
  const pyForecasts: Record<number, { en: string; hi: string }> = {
    1: {
      en: 'Year of New Beginnings: Plant new seeds, start ventures, initiate bold actions.',
      hi: 'नई शुरुआत का वर्ष: नए कार्य, व्यापार और जीवन में नए आयाम आरंभ करने का श्रेष्ठ समय।'
    },
    2: {
      en: 'Year of Cooperation & Patience: Focus on relationships, emotional harmony, and partnerships.',
      hi: 'सहयोग व धैर्य का वर्ष: संबंधों में मधुरता, साझेदारी और संयम से आगे बढ़ने का काल।'
    },
    3: {
      en: 'Year of Expansion & Creativity: Express your gifts, social networking, and joy.',
      hi: 'ज्ञान व रचनात्मक विस्तार का वर्ष: कला, शिक्षा और सामाजिक प्रतिष्ठा में वृद्धि।'
    },
    4: {
      en: 'Year of Hard Work & Foundations: Build security, organize affairs, and work diligently.',
      hi: 'कठिन परिश्रम व नींव का वर्ष: भविष्य के लिए ठोस व्यवस्था, अनुशासन और नियमबद्धता।'
    },
    5: {
      en: 'Year of Dynamic Transformation: Travel, sudden opportunities, versatility, and freedom.',
      hi: 'सकारात्मक परिवर्तन व यात्राओं का वर्ष: नए अवसर, व्यापारिक उन्नति और यात्रा के योग।'
    },
    6: {
      en: 'Year of Love, Family & Harmony: Domestic blessings, health care, and responsibilities.',
      hi: 'पारिवारिक सुख व ऐश्वर्य का वर्ष: गृहस्थ सुख, सौंदर्य और नए दायित्वों का निर्वहन।'
    },
    7: {
      en: 'Year of Rest & Spiritual Introspection: Research, study, meditation, and inner peace.',
      hi: 'आध्यात्मिक चिंतन व शोध का वर्ष: आत्म-मंथन, विद्या अध्ययन और मानसिक शांति।'
    },
    8: {
      en: 'Year of Material Harvest & Karma: Career power, financial investments, and executive rewards.',
      hi: 'कर्मफल व आर्थिक समृद्धि का वर्ष: कार्यक्षेत्र में बड़ी सफलता, धन लाभ और अधिकार प्राप्ति।'
    },
    9: {
      en: 'Year of Completion & Humanitarian Cleansing: Finish old cycles, forgive, and prepare for renewal.',
      hi: 'पूर्णता व नवीनीकरण का वर्ष: पुराने रुके कार्य पूर्ण होंगे, दान-पुण्य और नई ऊर्जा का संचार।'
    }
  };

  return {
    moolank,
    bhagyank,
    namankChaldean: chaldean.isValid ? chaldean.single : 0,
    namankPythagorean: pythagorean.isValid ? pythagorean.single : 0,
    isNameValid: chaldean.isValid,
    nameValidationMessage: chaldean.warning,
    kuaNumber,
    birthDateFormatted,
    isDateValid,
    dateValidationMessage: isDateValid ? undefined : 'Birth date could not be parsed; defaulted to standard baseline.',
    numerologyMethodology: 'Vedic Anka Shastra (Moolank/Bhagyank), Classical Chaldean Cheiro System (1-8 Name Vibrations), Pythagorean (1-9), and Chinese Lo Shu Magic Square & Ba Zhai Kua',
    traditionBadges: {
      moolank: 'Vedic Anka Shastra (वैदिक अंक शास्त्र)',
      bhagyank: 'Vedic Anka Shastra (वैदिक अंक शास्त्र)',
      namank: 'Chaldean & Pythagorean (चालडियन एवं पाइथागोरियन)',
      kua: 'Eight Mansions Feng Shui & Vastu (अष्ट दिशा वास्तु / कुआ)',
      loshu: 'Chinese Lo Shu Magic Square (लो-शू चक्र)'
    },
    moolankLord: NUMBER_LORDS[moolank]?.en || 'Sun',
    moolankLordHi: NUMBER_LORDS[moolank]?.hi || 'सूर्य',
    bhagyankLord: NUMBER_LORDS[bhagyank]?.en || 'Sun',
    bhagyankLordHi: NUMBER_LORDS[bhagyank]?.hi || 'सूर्य',
    namankLord: chaldean.isValid ? (NUMBER_LORDS[chaldean.single]?.en || 'Mercury') : '—',
    moolankTraits: {
      title: mData.title,
      titleHi: mData.titleHi,
      description: mData.desc,
      descriptionHi: mData.descHi,
      strengths: mData.strengths,
      strengthsHi: mData.strengthsHi,
      challenges: mData.challenges,
      challengesHi: mData.challengesHi
    },
    bhagyankTraits: {
      title: bData.title,
      titleHi: bData.titleHi,
      description: bData.desc,
      descriptionHi: bData.descHi,
      karmicMission: `Your ultimate life destiny is guided by ${bData.title}. Aligning your major career choices with this vibration unlocks effortless success.`,
      karmicMissionHi: `आपका अंतिम जीवन लक्ष्य ${bData.titleHi} से संचालित है। इसके अनुकूल कर्म करने से जीवन में निरंतर शुभ फल प्राप्त होते हैं।`
    },
    loshuGrid: loshu,
    luckyNumbers: relations.friends,
    neutralNumbers: relations.neutral,
    unluckyNumbers: relations.enemies,
    luckyColors: mData.colors,
    luckyColorsHi: mData.colorsHi,
    luckyDays: mData.days,
    luckyDaysHi: mData.daysHi,
    luckyDates: mData.dates,
    favorableDirections: ['East', 'North-East', 'North'],
    favorableDirectionsHi: ['पूर्व', 'उत्तर-पूर्व (ईशान)', 'उत्तर'],
    recommendedGemstone: mData.gemstone,
    recommendedGemstoneHi: mData.gemstoneHi,
    recommendedRudraksha: mData.rudraksha,
    recommendedMantra: mData.mantra,
    recommendedYantra: mData.yantra,
    personalYear: personalYearSum,
    personalYearForecast: pyForecasts[personalYearSum]?.en || '',
    personalYearForecastHi: pyForecasts[personalYearSum]?.hi || '',
    isMasterNumber,
    karmicDebtNumber
  };
}

export * from './nameNumerologyEngine';
